'use client';

import { useRef } from 'react';
import { mintKey } from './live-queue';

/**
 * **A create's idempotency key, minted at the gesture** (docs/LOADING.md §3.4,
 * docs/SYNC.md §7.4). A surface that lists a created row before the reply
 * offers "Not saved. Retry" when the reply is lost, and the retry resends the
 * SAME key: the write spine answers a key its log already holds with the item
 * the first arrival made (`createWorkItem`), so a retry never makes a second
 * item. The key is the offline queue's shape (`mintKey`: time, counter,
 * random), never `crypto.randomUUID`, which a phone on the LAN's plain http
 * does not have (.claude/rules/ui-kit.md).
 */
let counter = 0;

export function mintCreateKey(): string {
  counter += 1;
  return `c-${mintKey(counter, Date.now(), Math.random())}`;
}

/**
 * The key for a field that resubmits its own text as the retry (the board's
 * add bar keeps the typed title after a failure, and the person presses Enter
 * again): the same title after an unsettled attempt reuses that attempt's key;
 * a changed title, or any submit after `settle`, mints a fresh one.
 */
export function useRetryKey(): { keyFor: (title: string) => string; settle: () => void } {
  const last = useRef<{ title: string; key: string } | null>(null);
  return {
    keyFor(title: string) {
      if (last.current?.title === title) return last.current.key;
      const key = mintCreateKey();
      last.current = { title, key };
      return key;
    },
    settle() {
      last.current = null;
    },
  };
}
